import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Book, BookFilters } from '../types';
import { booksApi, tagsApi, authApi, TagsByCategory } from '../services/api';
import { FilterPanel } from './FilterPanel';
import { BookGrid } from './BookGrid';
import { BookDetailModal } from './BookDetailModal';
import './BookCatalogView.css';

// Lote de livros renderizados por vez (rolagem progressiva)
const PAGE_SIZE = 24;

/**
 * Componente BookCatalogView
 * 
 * Exibe o catálogo de livros para clientes com:
 * - Login obrigatório
 * - Painel de filtros (FilterPanel)
 * - Grade de livros (BookGrid)
 * - Modal de detalhes (BookDetailModal)
 * - Estados de loading e erro
 * 
 * Valida: Requisitos 4.1, 4.2, 10.1, 10.2, 10.3, 10.4
 */
export function BookCatalogView(): React.ReactElement {
  // Estado de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [currentUser, setCurrentUser] = useState<{ username: string; displayName?: string; role?: string } | null>(null);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Estado dos livros (todos os livros carregados)
  const [allBooks, setAllBooks] = useState<Book[]>([]);
  const [isLoadingBooks, setIsLoadingBooks] = useState(true);
  const [booksError, setBooksError] = useState<string | null>(null);

  // Estado das tags para filtros
  const [tagsByCategory, setTagsByCategory] = useState<TagsByCategory[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);

  // Estado dos filtros ativos
  const [activeFilters, setActiveFilters] = useState<BookFilters>({});

  // Renderização progressiva da grade
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Estado do modal de detalhes
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Estado do painel de filtros (mobile)
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Verificar autenticação ao montar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const status = await authApi.getStatus();
        if (status.isAuthenticated && status.user) {
          setIsAuthenticated(true);
          setCurrentUser(status.user as any);
        }
      } catch {
        // Não autenticado
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, []);

  // Handler de login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoggingIn(true);
    try {
      const result = await authApi.login(loginUsername, loginPassword);
      setIsAuthenticated(true);
      setCurrentUser(result.user as any);
    } catch (err: any) {
      setLoginError(err?.message || 'Usuário ou senha inválidos');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Handler de logout
  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignorar erro de logout
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setAllBooks([]);
  };

  // Carrega tags ao montar o componente (só se autenticado)
  useEffect(() => {
    if (!isAuthenticated) return;
    const loadTags = async () => {
      setIsLoadingTags(true);
      try {
        const data = await tagsApi.getAllByCategory();
        setTagsByCategory(data);
      } catch (error) {
        console.error('Erro ao carregar tags:', error);
      } finally {
        setIsLoadingTags(false);
      }
    };
    loadTags();
  }, [isAuthenticated]);

  // Carrega todos os livros uma vez ao montar
  const loadBooks = useCallback(async () => {
    setIsLoadingBooks(true);
    setBooksError(null);
    try {
      const data = await booksApi.getAll();
      setAllBooks(data);
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message || 'Erro ao carregar livros. Tente novamente.';
      setBooksError(errorMessage);
    } finally {
      setIsLoadingBooks(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadBooks();
  }, [loadBooks, isAuthenticated]);

  // Filtra livros localmente (sem chamar API)
  const filteredBooks = useMemo(() => {
    let result = allBooks;

    // Filtro por texto (título ou descrição)
    if (activeFilters.search && activeFilters.search.trim()) {
      const searchTerm = activeFilters.search.trim().toLowerCase();
      result = result.filter(book => 
        book.title.toLowerCase().includes(searchTerm) ||
        book.description.toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por disponibilidade física
    if (activeFilters.physicallyAvailable !== undefined) {
      result = result.filter(book => book.physicallyAvailable === activeFilters.physicallyAvailable);
    }

    // Filtro por livros premiados
    if (activeFilters.isAwarded !== undefined) {
      result = result.filter(book => book.isAwarded === activeFilters.isAwarded);
    }

    // Filtro por tags
    if (activeFilters.tags && activeFilters.tags.length > 0) {
      result = result.filter(book => {
        if (!book.tags) return false;
        const bookTagIds = book.tags.map(tag => tag.id);
        return activeFilters.tags!.every(tagId => bookTagIds.includes(tagId));
      });
    }

    return result;
  }, [allBooks, activeFilters]);

  // Conta livros por tag em uma única passada sobre filteredBooks
  const tagCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const book of filteredBooks) {
      if (!book.tags) continue;
      // Tags distintas do livro (mesma semântica da contagem anterior)
      for (const tagId of new Set(book.tags.map(tag => tag.id))) {
        counts.set(tagId, (counts.get(tagId) || 0) + 1);
      }
    }
    return counts;
  }, [filteredBooks]);

  // Recalcula contadores das tags baseado nos livros filtrados
  const tagsWithDynamicCounts = useMemo(() => {
    return tagsByCategory.map(categoryData => ({
      ...categoryData,
      tags: categoryData.tags.map(tag => ({
        ...tag,
        bookCount: tagCounts.get(tag.id) || 0
      }))
    }));
  }, [tagsByCategory, tagCounts]);

  // Fatia visível da lista filtrada (renderização progressiva)
  const visibleBooks = useMemo(
    () => filteredBooks.slice(0, visibleCount),
    [filteredBooks, visibleCount]
  );
  const hasMoreBooks = visibleBooks.length < filteredBooks.length;

  // Volta para a primeira página ao trocar filtros ou recarregar os livros
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeFilters, allBooks]);

  // Carrega mais livros (botão de fallback)
  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => prev + PAGE_SIZE);
  }, []);

  // Observa o sentinel: quando ele se aproxima da viewport, carrega o próximo lote
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    if (visibleCount >= filteredBooks.length) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisibleCount(prev => prev + PAGE_SIZE);
        }
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, filteredBooks.length]);

  // Handler para mudança de filtros
  const handleFilterChange = useCallback((filters: BookFilters) => {
    setActiveFilters(filters);
  }, []);

  // Handler para clique em livro
  const handleBookClick = useCallback((book: Book) => {
    setSelectedBook(book);
    setIsModalOpen(true);
  }, []);

  // Handler para fechar modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBook(null);
  }, []);

  // Handler para toggle do painel de filtros (mobile)
  const toggleFilterPanel = useCallback(() => {
    setIsFilterPanelOpen((prev) => !prev);
  }, []);

  // Conta filtros ativos
  const activeFilterCount =
    (activeFilters.tags?.length || 0) +
    (activeFilters.physicallyAvailable !== undefined ? 1 : 0) +
    (activeFilters.isAwarded !== undefined ? 1 : 0) +
    (activeFilters.search?.trim() ? 1 : 0);

  return (
    <div className="book-catalog-view">
      {/* Tela de loading de autenticação */}
      {isCheckingAuth && (
        <div className="book-catalog-view__auth-loading">
          <div className="book-catalog-view__spinner" />
          <p>Verificando sessão...</p>
        </div>
      )}

      {/* Tela de login */}
      {!isCheckingAuth && !isAuthenticated && (
        <div className="book-catalog-view__login">
          <div className="book-catalog-view__login-card">
            <img src="/images/logo png.png" alt="Ipsis" className="book-catalog-view__login-logo" />
            <h1 className="book-catalog-view__login-title">Estante Virtual</h1>
            <p className="book-catalog-view__login-subtitle">Faça login para acessar o catálogo</p>
            <form className="book-catalog-view__login-form" onSubmit={handleLogin}>
              {loginError && (
                <div className="book-catalog-view__login-error">{loginError}</div>
              )}
              <div className="book-catalog-view__login-field">
                <label htmlFor="catalog-username">Usuário</label>
                <input
                  id="catalog-username"
                  type="text"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                  disabled={isLoggingIn}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="book-catalog-view__login-field">
                <label htmlFor="catalog-password">Senha</label>
                <input
                  id="catalog-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  disabled={isLoggingIn}
                  autoComplete="current-password"
                  required
                />
              </div>
              <button
                type="submit"
                className="book-catalog-view__login-button"
                disabled={isLoggingIn || !loginUsername.trim() || !loginPassword.trim()}
              >
                {isLoggingIn ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Catálogo (só aparece se autenticado) */}
      {!isCheckingAuth && isAuthenticated && (
        <>
          {/* Header */}
          <header className="book-catalog-view__header">
            <div className="book-catalog-view__header-left">
              <img 
                src="/images/logo png.png" 
                alt="Ipsis" 
                className="book-catalog-view__logo"
              />
              <h1 className="book-catalog-view__title">Estante Virtual</h1>
            </div>
            <div className="book-catalog-view__header-right">
              <button
                type="button"
                className="book-catalog-view__filter-toggle"
                onClick={toggleFilterPanel}
                aria-expanded={isFilterPanelOpen}
                aria-controls="filter-panel"
              >
                <span className="book-catalog-view__filter-icon">🔍</span>
                <span className="book-catalog-view__filter-text">Filtros</span>
                {activeFilterCount > 0 && (
                  <span className="book-catalog-view__filter-badge">{activeFilterCount}</span>
                )}
              </button>
              <span className="book-catalog-view__user-name">
                {currentUser?.displayName || currentUser?.username}
              </span>
              <button
                type="button"
                className="book-catalog-view__logout-button"
                onClick={handleLogout}
              >
                Sair
              </button>
            </div>
          </header>

      <div className="book-catalog-view__content">
        {/* Painel de Filtros */}
        <aside
          id="filter-panel"
          className={`book-catalog-view__sidebar ${isFilterPanelOpen ? 'book-catalog-view__sidebar--open' : ''}`}
        >
          <div className="book-catalog-view__sidebar-header">
            <h2 className="book-catalog-view__sidebar-title">Filtros</h2>
            <button
              type="button"
              className="book-catalog-view__sidebar-close"
              onClick={toggleFilterPanel}
              aria-label="Fechar filtros"
            >
              ✕
            </button>
          </div>
          {!isLoadingTags && (
            <FilterPanel
              availableTags={tagsWithDynamicCounts}
              onFilterChange={handleFilterChange}
              bookCount={filteredBooks.length}
              isLoading={isLoadingBooks}
              onClose={toggleFilterPanel}
            />
          )}
        </aside>

        {/* Overlay para mobile quando filtros estão abertos */}
        {isFilterPanelOpen && (
          <div
            className="book-catalog-view__overlay"
            onClick={toggleFilterPanel}
            aria-hidden="true"
          />
        )}

        {/* Área principal com grade de livros */}
        <main className="book-catalog-view__main">
          {booksError ? (
            <div className="book-catalog-view__error">
              <span className="book-catalog-view__error-icon">⚠️</span>
              <p className="book-catalog-view__error-message">{booksError}</p>
              <button
                type="button"
                className="book-catalog-view__retry-button"
                onClick={loadBooks}
              >
                Tentar novamente
              </button>
            </div>
          ) : (
            <>
              <BookGrid
                books={visibleBooks}
                onBookClick={handleBookClick}
                isLoading={isLoadingBooks}
                emptyMessage={
                  activeFilterCount > 0
                    ? 'Nenhum livro encontrado com os filtros selecionados.'
                    : 'Nenhum livro cadastrado ainda.'
                }
              />
              {!isLoadingBooks && hasMoreBooks && (
              <div
                className="book-catalog-view__load-more"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.5rem 1rem 2rem',
                }}
              >
                <span
                  className="book-catalog-view__showing-count"
                  style={{ fontSize: '0.9rem', color: '#64748b' }}
                >
                  Exibindo {visibleBooks.length} de {filteredBooks.length} livros
                </span>
                <div
                  ref={sentinelRef}
                  aria-hidden="true"
                  style={{ height: '1px', width: '100%' }}
                />
                <button
                  type="button"
                  className="book-catalog-view__retry-button"
                  onClick={handleLoadMore}
                >
                  Carregar mais
                </button>
              </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Modal de Detalhes do Livro */}
      <BookDetailModal
        book={selectedBook}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
        </>
      )}
    </div>
  );
}

export default BookCatalogView;
